import { apiRequest } from './apiClient';
import { ApiError } from '@/models/errors/ApiError';

export type PaymentsLowBalanceSetting = 'accept_smaller' | 'reject_smaller' | 'manual_review';
export type PaymentsOutstandingBalanceSetting = 'spread_amongst' | 'add_as_additional_payment' | 'manual_review';

export interface SystemPreferences {
    paymentsLowBalanceSettings: PaymentsLowBalanceSetting | null;
    paymentsOutstandingBalanceSettings: PaymentsOutstandingBalanceSetting | null;
    negotiationsDefaultSettlementOffer: number | null;
    negotiationsDefaultNumberOfPayments: number | null;
}

type SystemPreferencesApiResponse = {
    payments_low_balance_settings: PaymentsLowBalanceSetting | null;
    payments_outstanding_balance_settings: PaymentsOutstandingBalanceSetting | null;
    negotiations_default_settlement_offer: number | null;
    negotiations_default_number_of_payments: number | null;
    updated_at: string | null;
};

type SystemPreferencesApiPayload = {
    payments_low_balance_settings?: PaymentsLowBalanceSetting;
    payments_outstanding_balance_settings?: PaymentsOutstandingBalanceSetting;
    negotiations_default_settlement_offer?: number;
    negotiations_default_number_of_payments?: number;
};

function mapFromApi(data: SystemPreferencesApiResponse): SystemPreferences {
    return {
        paymentsLowBalanceSettings: data.payments_low_balance_settings,
        paymentsOutstandingBalanceSettings: data.payments_outstanding_balance_settings,
        negotiationsDefaultSettlementOffer: data.negotiations_default_settlement_offer,
        negotiationsDefaultNumberOfPayments: data.negotiations_default_number_of_payments,
    };
}

function mapToApi(data: SystemPreferences): SystemPreferencesApiPayload {
    const payload: SystemPreferencesApiPayload = {};

    if (data.paymentsLowBalanceSettings !== null) {
        payload.payments_low_balance_settings = data.paymentsLowBalanceSettings;
    }
    if (data.paymentsOutstandingBalanceSettings !== null) {
        payload.payments_outstanding_balance_settings = data.paymentsOutstandingBalanceSettings;
    }
    if (data.negotiationsDefaultSettlementOffer !== null) {
        payload.negotiations_default_settlement_offer = data.negotiationsDefaultSettlementOffer;
    }
    if (data.negotiationsDefaultNumberOfPayments !== null) {
        payload.negotiations_default_number_of_payments = data.negotiationsDefaultNumberOfPayments;
    }

    return payload;
}

export async function getSystemPreferences(): Promise<SystemPreferences> {
    const response = await apiRequest<{ data: SystemPreferencesApiResponse }>(
        '/api/company/settings/system-preferences',
        'GET',
    );
    return mapFromApi(response.data);
}

export async function saveSystemPreferences(
    data: SystemPreferences,
): Promise<SystemPreferences> {
    try {
        const response = await apiRequest<{ data: SystemPreferencesApiResponse }>(
            '/api/company/settings/system-preferences',
            'POST',
            { body: mapToApi(data) },
        );
        return mapFromApi(response.data);
    } catch (error) {
        const apiError = ApiError.from(error);
        if (apiError?.status === 422) {
            throw apiError.toValidationError({
                payments_low_balance_settings: 'paymentsLowBalanceSettings',
                payments_outstanding_balance_settings: 'paymentsOutstandingBalanceSettings',
                negotiations_default_settlement_offer: 'negotiationsDefaultSettlementOffer',
                negotiations_default_number_of_payments: 'negotiationsDefaultNumberOfPayments',
            });
        }
        throw error;
    }
}
